/**
 * @author      yontaek@zumper.com (Yon Taek Chung)
 * @copyright   Copyright (c) 2022, Zumper
 * @description PreserveFiltersLink Container
 */

import { ListingAmenity, PropertyFeatures, PropertyType } from '@zcode/enums'
import {
  BEDROOMS_RE,
  NIGHTLY_OVER_RE,
  NIGHTLY_PRICE_RANGE_RE,
  NIGHTLY_UNDER_RE,
  OVER_RE,
  PRICE_RANGE_RE,
  STUDIO_RE,
  UNDER_RE,
} from '@zcode/nr-utils'
import type { Filters } from '@zcode/state-type'
import type { Any, O } from 'ts-toolbelt'
import { logger } from '../../../../logger'
import { OR_POOL_KEY, OR_POOL_LISTING_KEY } from './amenitiesConstants'
import { MAX_FILTER_PRICE, MAX_NIGHTLY_FILTER_PRICE, MIN_FILTER_PRICE } from './filterConstants'
import { APARTMENT_TYPE_GROUP, CONDO_TYPE_GROUP, HOUSE_TYPE_GROUP } from './propertyTypesConstants'

const { FURNISHED } = ListingAmenity
const { INCOME_RESTRICTED, NO_SECURITY_DEPOSIT, SPECIALS, UTILITIES_INCLUDED } = PropertyFeatures

// NOTE: Make sure to update relevant python utils when adding new filter verticals
// Relevant utils are in zcode biz_utils/api.py and include
// get_path_from_filters and parse_query_from_path
export const getFiltersFromUrlFiltersPathHelper = (
  path: string,
  newFilters: Partial<O.Writable<Filters, Any.Key, 'deep'>> = {}
) => {
  let m
  const parts = path[0] === '/' ? path.slice(1).split('/') : path.split('/')
  for (let i = 0, leni = parts.length; i < leni; ++i) {
    const part = parts[i]
    if (part === 'furnished') {
      if (newFilters.listingAmenities === undefined) {
        newFilters.listingAmenities = []
      }
      newFilters.listingAmenities = newFilters.listingAmenities.concat(FURNISHED)
    } else if (part === 'short-term') {
      newFilters.longTerm = false
      newFilters.shortTerm = true
    } else if (part === 'luxury') {
      // don't let elevated listings force their way in,  9-16-2021  -Hank
      newFilters.fillElevated = false
      newFilters.minPricePerBedroomPercentile = 'p75'
    } else if (part === 'cheap') {
      // don't let elevated listings force their way in,  9-16-2021  -Hank
      newFilters.fillElevated = false
      newFilters.maxPricePerBedroomPercentile = 'p50'
    } else if (part === 'no-fee') {
      newFilters.noFees = true
    } else if (part === 'no-security-deposit') {
      const orFeatures = newFilters.orFeatures || []
      const dealsArr = orFeatures[0] ?? []
      dealsArr.push(NO_SECURITY_DEPOSIT)
      newFilters.orFeatures = [dealsArr]
    } else if (part === 'low-income') {
      const features = newFilters.features || []
      features.push(INCOME_RESTRICTED)
      newFilters.features = features
    } else if (part === 'move-in-special') {
      const orFeatures = newFilters.orFeatures || []
      const dealsArr = orFeatures[0] ?? []
      dealsArr.push(SPECIALS)
      newFilters.orFeatures = [dealsArr]
    } else if (part === 'utilities-included') {
      const orFeatures = newFilters.orFeatures || []
      const dealsArr = orFeatures[0] ?? []
      dealsArr.push(UTILITIES_INCLUDED)
      newFilters.orFeatures = [dealsArr]
    } else if (part === 'pet-friendly') {
      newFilters.cats = true
      newFilters.dogs = true
    } else if (part === 'dog-friendly') {
      newFilters.dogs = true
    } else if (part === 'cat-friendly') {
      newFilters.cats = true
    } else if (part === 'today') {
      newFilters.maxDays = 1
    } else if (part === 'short-term') {
      newFilters.shortTerm = true
      newFilters.longTerm = false
    } else if (part === 'cabins') {
      if (newFilters.propertyTypes === undefined) {
        newFilters.propertyTypes = []
      }
      newFilters.propertyTypes = newFilters.propertyTypes.concat(PropertyType.CABINS)
    } else if (part === 'cottage') {
      if (newFilters.propertyTypes === undefined) {
        newFilters.propertyTypes = []
      }
      newFilters.propertyTypes = newFilters.propertyTypes.concat(PropertyType.COTTAGE)
    } else if (part === 'villas') {
      if (newFilters.propertyTypes === undefined) {
        newFilters.propertyTypes = []
      }
      newFilters.propertyTypes = newFilters.propertyTypes.concat(PropertyType.VILLA)
    } else if (part === 'mansions') {
      if (newFilters.propertyTypes === undefined) {
        newFilters.propertyTypes = []
      }
      newFilters.propertyTypes = newFilters.propertyTypes.concat(PropertyType.MANSIONS)
    } else if (part === 'bungalow') {
      if (newFilters.propertyTypes === undefined) {
        newFilters.propertyTypes = []
      }
      newFilters.propertyTypes = newFilters.propertyTypes.concat(PropertyType.BUNGALOW)
    } else if (part === 'house-boat') {
      if (newFilters.propertyTypes === undefined) {
        newFilters.propertyTypes = []
      }
      newFilters.propertyTypes = newFilters.propertyTypes.concat(PropertyType.HOUSEBOAT)
    } else if (part === 'guest-house') {
      if (newFilters.propertyTypes === undefined) {
        newFilters.propertyTypes = []
      }
      newFilters.propertyTypes = newFilters.propertyTypes.concat(PropertyType.GUEST_HOUSE)
    } else if (part === 'hot-tub') {
      if (newFilters.listingAmenities === undefined) {
        newFilters.listingAmenities = []
      }
      newFilters.listingAmenities = newFilters.listingAmenities.concat(48)
    } else if (part === 'pool') {
      if (newFilters.orAmenities === undefined) {
        newFilters.orAmenities = []
      }
      newFilters.orAmenities = newFilters.orAmenities.concat([[OR_POOL_KEY, OR_POOL_LISTING_KEY]])
    } else if (part === 'condos-and-apartments') {
      if (newFilters.propertyTypes === undefined) {
        newFilters.propertyTypes = []
      }
      newFilters.propertyTypes = newFilters.propertyTypes.concat([
        ...APARTMENT_TYPE_GROUP,
        ...CONDO_TYPE_GROUP,
      ])
    } else if (part === 'house-rentals') {
      if (newFilters.propertyTypes === undefined) {
        newFilters.propertyTypes = []
      }
      newFilters.propertyTypes = newFilters.propertyTypes.concat(HOUSE_TYPE_GROUP)
    } else if (part === 'private-pool') {
      if (newFilters.listingAmenities === undefined) {
        newFilters.listingAmenities = []
      }
      newFilters.listingAmenities = newFilters.listingAmenities.concat(40)
    } else if (part === 'heated-pool-or-indoor-pool') {
      if (newFilters.listingAmenities === undefined) {
        newFilters.listingAmenities = []
      }
      newFilters.listingAmenities = newFilters.listingAmenities.concat(41)
    } else if (part === 'beach') {
      if (newFilters.listingAmenities === undefined) {
        newFilters.listingAmenities = []
      }
      newFilters.listingAmenities = newFilters.listingAmenities.concat(42)
    } else if (part === 'lake') {
      if (newFilters.listingAmenities === undefined) {
        newFilters.listingAmenities = []
      }
      newFilters.listingAmenities = newFilters.listingAmenities.concat(43)
    } else if (part === 'waterfront') {
      if (newFilters.listingAmenities === undefined) {
        newFilters.listingAmenities = []
      }
      newFilters.listingAmenities = newFilters.listingAmenities.concat(44)
    } else if (part === 'mountains') {
      if (newFilters.listingAmenities === undefined) {
        newFilters.listingAmenities = []
      }
      newFilters.listingAmenities = newFilters.listingAmenities.concat(45)
    } else if (part === 'oceanfront') {
      if (newFilters.listingAmenities === undefined) {
        newFilters.listingAmenities = []
      }
      newFilters.listingAmenities = newFilters.listingAmenities.concat(46)
    } else if (part === 'ski-in-ski-out') {
      if (newFilters.listingAmenities === undefined) {
        newFilters.listingAmenities = []
      }
      newFilters.listingAmenities = newFilters.listingAmenities.concat(47)
    } else if ((m = STUDIO_RE.exec(part))) {
      if (newFilters.bedrooms === undefined) {
        newFilters.bedrooms = [false, false, false, false, false, false]
      }
      if (m[1]) {
        newFilters.bedrooms = [true, true, true, true, true, true]
      } else {
        newFilters.bedrooms[0] = true
      }
    } else if ((m = NIGHTLY_UNDER_RE.exec(part))) {
      newFilters.maxNightlyPrice = Math.max(~~m[1], MIN_FILTER_PRICE)
      if (!newFilters.minNightlyPrice) {
        newFilters.minNightlyPrice = 0
      }
      if (newFilters.maxNightlyPrice === MAX_NIGHTLY_FILTER_PRICE) {
        newFilters.customMaxPrice = true
      }
    } else if ((m = NIGHTLY_OVER_RE.exec(part))) {
      newFilters.minNightlyPrice = Math.min(~~m[1], MAX_NIGHTLY_FILTER_PRICE)
      if (!newFilters.maxNightlyPrice) {
        newFilters.maxNightlyPrice = 1000
      }
    } else if ((m = NIGHTLY_PRICE_RANGE_RE.exec(part))) {
      m = m[1].split(',')
      newFilters.minNightlyPrice = Math.max(~~m[0], MIN_FILTER_PRICE)
      newFilters.maxNightlyPrice = ~~(m[1] || MAX_NIGHTLY_FILTER_PRICE)
    } else if ((m = UNDER_RE.exec(part))) {
      newFilters.maxPrice = Math.max(~~m[1], MIN_FILTER_PRICE)
      if (!newFilters.minPrice) {
        newFilters.minPrice = 0
      }
      if (newFilters.maxPrice === MAX_FILTER_PRICE) {
        newFilters.customMaxPrice = true
      }
    } else if ((m = OVER_RE.exec(part))) {
      newFilters.minPrice = Math.min(~~m[1], MAX_FILTER_PRICE)
      if (!newFilters.maxPrice) {
        newFilters.maxPrice = MAX_FILTER_PRICE
      }
    } else if ((m = PRICE_RANGE_RE.exec(part))) {
      // price range
      // TODO(woody) This is my ghetto fix, the regex should be good enough to split the comma
      m = m[1].split(',')
      newFilters.minPrice = Math.max(~~m[0], MIN_FILTER_PRICE)
      newFilters.maxPrice = ~~(m[1] || MAX_FILTER_PRICE)
    } else if ((m = BEDROOMS_RE.exec(part))) {
      if (newFilters.bedrooms === undefined) {
        newFilters.bedrooms = [false, false, false, false, false, false]
      }
      const nrs = (m[1] || m[2])
        .replace(/studios?/, '0')
        .replace(/[^\d,+]+/g, '')
        .split(/,+/)
      for (let j = 0, lenj = nrs.length; j < lenj; ++j) {
        let br = nrs[j]
        if (br) {
          const ending = br[br.length - 1]
          br = ending === '+' ? br.slice(0, br.length - 1) : br
          if (ending === '+') {
            for (let k = ~~br; k <= 5; ++k) {
              newFilters.bedrooms[k] = true
            }
          } else {
            newFilters.bedrooms[~~br] = true
          }
        }
      }
    } else {
      logger.warn('getFiltersFromUrlFiltersPath: unrecognized', i, part)
    }
  }
  // TODO move this to the slider component
  // if min and max price are same as the MAX_FILTER_PRICE, change the viewValue of filter's
  // min price to be 10% smaller than the MAX_FILTER_PRICE so that the sliders doesn't get
  // stuck on 5000.
  if (
    newFilters.maxPrice &&
    newFilters.maxPrice === MAX_FILTER_PRICE &&
    newFilters.minPrice &&
    newFilters.minPrice === MAX_FILTER_PRICE
  ) {
    newFilters.minPrice -= (MIN_FILTER_PRICE + MAX_FILTER_PRICE) * 0.1
  } else if (
    typeof newFilters.maxPrice !== 'undefined' &&
    typeof newFilters.minPrice !== 'undefined' &&
    newFilters.maxPrice < newFilters.minPrice
  ) {
    // Check if prices are invalid range
    newFilters.minPrice = MIN_FILTER_PRICE
    newFilters.maxPrice = MAX_FILTER_PRICE
  }
  return newFilters
}
